import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Navbar } from '../components';
import { Footer } from '../containers';
import SEOMeta from '../components/SEOMeta/SEOMeta';
import {
  generateOrganizationSchema,
  generateLocalBusinessSchema,
} from '../utils/seoSchema';
import { db } from '../firebaseconfig';
import { useAuth } from '../context/AuthContext';
import './DSALabPage.css';

const PROGRESS_STORAGE_KEY = 'algodoor_dsa_progress';

const TEMPLATE_LIBRARY = {
  twoSum: {
    pattern: 'Two Sum Pattern',
    visualType: 'array-pointer',
    functionName: 'twoSum',
    jsStarter: 'function twoSum(nums, target) {\n  const seen = new Map();\n  for (let i = 0; i < nums.length; i += 1) {\n    const need = target - nums[i];\n    if (seen.has(need)) return [seen.get(need), i];\n    seen.set(nums[i], i);\n  }\n  return [];\n}',
    javaStarter: 'class Solution {\n  public static int[] twoSum(int[] nums, int target) {\n    java.util.Map<Integer, Integer> seen = new java.util.HashMap<>();\n    for (int i = 0; i < nums.length; i++) {\n      int need = target - nums[i];\n      if (seen.containsKey(need)) return new int[] {seen.get(need), i};\n      seen.put(nums[i], i);\n    }\n    return new int[] {};\n  }\n}',
    tests: [
      { input: [[2, 7, 11, 15], 9], expected: [0, 1] },
      { input: [[3, 2, 4], 6], expected: [1, 2] },
      { input: [[3, 3], 6], expected: [0, 1] },
    ],
    statement: 'Return indices of the two numbers that add up to target.',
  },
  stack: {
    pattern: 'Stack Pattern',
    visualType: 'stack',
    functionName: 'isValid',
    jsStarter: 'function isValid(s) {\n  const pairs = new Map([[\")\", \"(\"], [\"]\", \"[\"], [\"}\", \"{\"]]);\n  const stack = [];\n  for (const ch of s) {\n    if (pairs.has(ch)) {\n      if (stack.pop() !== pairs.get(ch)) return false;\n    } else {\n      stack.push(ch);\n    }\n  }\n  return stack.length === 0;\n}',
    javaStarter: 'class Solution {\n  public static boolean isValid(String s) {\n    java.util.Map<Character, Character> pairs = new java.util.HashMap<>();\n    pairs.put(\')\', \'(\');\n    pairs.put(\']\', \'[\');\n    pairs.put(\'}\', \'{\');\n    java.util.ArrayDeque<Character> stack = new java.util.ArrayDeque<>();\n    for (char ch : s.toCharArray()) {\n      if (pairs.containsKey(ch)) {\n        if (stack.isEmpty() || stack.pop() != pairs.get(ch)) return false;\n      } else {\n        stack.push(ch);\n      }\n    }\n    return stack.isEmpty();\n  }\n}',
    tests: [
      { input: ['()[]{}'], expected: true },
      { input: ['(]'], expected: false },
      { input: ['([{}])'], expected: true },
    ],
    statement: 'Check whether a bracket string is valid using a stack.',
  },
  binarySearch: {
    pattern: 'Binary Search Pattern',
    visualType: 'binary-search',
    functionName: 'search',
    jsStarter: 'function search(nums, target) {\n  let left = 0;\n  let right = nums.length - 1;\n  while (left <= right) {\n    const mid = Math.floor((left + right) / 2);\n    if (nums[mid] === target) return mid;\n    if (nums[mid] < target) left = mid + 1;\n    else right = mid - 1;\n  }\n  return -1;\n}',
    javaStarter: 'class Solution {\n  public static int search(int[] nums, int target) {\n    int left = 0;\n    int right = nums.length - 1;\n    while (left <= right) {\n      int mid = left + (right - left) / 2;\n      if (nums[mid] == target) return mid;\n      if (nums[mid] < target) left = mid + 1;\n      else right = mid - 1;\n    }\n    return -1;\n  }\n}',
    tests: [
      { input: [[-1, 0, 3, 5, 9, 12], 9], expected: 4 },
      { input: [[-1, 0, 3, 5, 9, 12], 2], expected: -1 },
      { input: [[1], 1], expected: 0 },
    ],
    statement: 'Return target index in sorted array using binary search.',
  },
  slidingWindow: {
    pattern: 'Sliding Window Pattern',
    visualType: 'window',
    functionName: 'maxProfit',
    jsStarter: 'function maxProfit(prices) {\n  let minPrice = Infinity;\n  let best = 0;\n  for (const price of prices) {\n    minPrice = Math.min(minPrice, price);\n    best = Math.max(best, price - minPrice);\n  }\n  return best;\n}',
    javaStarter: 'class Solution {\n  public static int maxProfit(int[] prices) {\n    int minPrice = Integer.MAX_VALUE;\n    int best = 0;\n    for (int price : prices) {\n      minPrice = Math.min(minPrice, price);\n      best = Math.max(best, price - minPrice);\n    }\n    return best;\n  }\n}',
    tests: [
      { input: [[7, 1, 5, 3, 6, 4]], expected: 5 },
      { input: [[7, 6, 4, 3, 1]], expected: 0 },
      { input: [[2, 4, 1]], expected: 2 },
    ],
    statement: 'Track best value while iterating with a running window perspective.',
  },
  intervals: {
    pattern: 'Intervals Pattern',
    visualType: 'intervals',
    functionName: 'merge',
    jsStarter: 'function merge(intervals) {\n  if (intervals.length <= 1) return intervals;\n  intervals.sort((a, b) => a[0] - b[0]);\n  const merged = [intervals[0]];\n  for (let i = 1; i < intervals.length; i += 1) {\n    const current = intervals[i];\n    const last = merged[merged.length - 1];\n    if (current[0] <= last[1]) last[1] = Math.max(last[1], current[1]);\n    else merged.push(current);\n  }\n  return merged;\n}',
    javaStarter: 'class Solution {\n  public static int[][] merge(int[][] intervals) {\n    if (intervals.length <= 1) return intervals;\n    java.util.Arrays.sort(intervals, java.util.Comparator.comparingInt(a -> a[0]));\n    java.util.List<int[]> merged = new java.util.ArrayList<>();\n    merged.add(intervals[0]);\n    for (int i = 1; i < intervals.length; i++) {\n      int[] current = intervals[i];\n      int[] last = merged.get(merged.size() - 1);\n      if (current[0] <= last[1]) last[1] = Math.max(last[1], current[1]);\n      else merged.add(current);\n    }\n    return merged.toArray(new int[0][]);\n  }\n}',
    tests: [
      { input: [[[1, 3], [2, 6], [8, 10], [15, 18]]], expected: [[1, 6], [8, 10], [15, 18]] },
      { input: [[[1, 4], [4, 5]]], expected: [[1, 5]] },
      { input: [[[1, 4], [0, 2], [3, 5]]], expected: [[0, 5]] },
    ],
    statement: 'Merge overlapping intervals after sorting by start time.',
  },
  dp: {
    pattern: 'Dynamic Programming Pattern',
    visualType: 'dp',
    functionName: 'climbStairs',
    jsStarter: 'function climbStairs(n) {\n  if (n <= 2) return n;\n  let prev = 1;\n  let curr = 2;\n  for (let i = 3; i <= n; i += 1) {\n    const next = prev + curr;\n    prev = curr;\n    curr = next;\n  }\n  return curr;\n}',
    javaStarter: 'class Solution {\n  public static int climbStairs(int n) {\n    if (n <= 2) return n;\n    int prev = 1;\n    int curr = 2;\n    for (int i = 3; i <= n; i++) {\n      int next = prev + curr;\n      prev = curr;\n      curr = next;\n    }\n    return curr;\n  }\n}',
    tests: [
      { input: [2], expected: 2 },
      { input: [3], expected: 3 },
      { input: [5], expected: 8 },
    ],
    statement: 'Build answer from previous solved states.',
  },
};

const INTERVIEW_TRACKS = [
  {
    section: 'Two Sum Family',
    template: 'twoSum',
    companies: ['Google', 'Amazon', 'Microsoft', 'Meta'],
    difficulty: 'Easy',
    questions: [
      'Two Sum', 'Two Sum II - Input Array Is Sorted', 'Two Number Sum', 'Pair Sum in Unsorted Array',
      'Pair Difference K', 'Subarray Sum Equals K', 'Count Pairs With Given Sum', 'Target Sum Pair in BST',
    ],
  },
  {
    section: 'Sliding Window Family',
    template: 'slidingWindow',
    companies: ['Amazon', 'Netflix', 'LinkedIn', 'Adobe'],
    difficulty: 'Medium',
    questions: [
      'Best Time to Buy and Sell Stock', 'Maximum Average Subarray I', 'Longest Substring Without Repeating Characters',
      'Minimum Size Subarray Sum', 'Permutation in String', 'Find All Anagrams in a String',
      'Longest Repeating Character Replacement', 'Minimum Window Substring',
    ],
  },
  {
    section: 'Stack Family',
    template: 'stack',
    companies: ['Amazon', 'Uber', 'Bloomberg', 'PayPal'],
    difficulty: 'Easy',
    questions: [
      'Valid Parentheses', 'Min Stack', 'Evaluate Reverse Polish Notation', 'Daily Temperatures',
      'Next Greater Element I', 'Largest Rectangle in Histogram', 'Asteroid Collision', 'Decode String',
    ],
  },
  {
    section: 'Binary Search Family',
    template: 'binarySearch',
    companies: ['Google', 'Apple', 'Uber', 'Atlassian'],
    difficulty: 'Medium',
    questions: [
      'Binary Search', 'Search Insert Position', 'First Bad Version', 'Search in Rotated Sorted Array',
      'Find Minimum in Rotated Sorted Array', 'Koko Eating Bananas', 'Peak Index in Mountain Array', 'Median of Two Sorted Arrays',
    ],
  },
  {
    section: 'Intervals Family',
    template: 'intervals',
    companies: ['Meta', 'Google', 'Salesforce', 'Oracle'],
    difficulty: 'Medium',
    questions: [
      'Merge Intervals', 'Insert Interval', 'Non-overlapping Intervals', 'Meeting Rooms II',
      'Interval List Intersections', 'Employee Free Time', 'Minimum Number of Arrows to Burst Balloons', 'Car Pooling',
    ],
  },
  {
    section: 'Dynamic Programming Family',
    template: 'dp',
    companies: ['Microsoft', 'Amazon', 'Adobe', 'Intuit'],
    difficulty: 'Medium',
    questions: [
      'Climbing Stairs', 'House Robber', 'Coin Change', 'Decode Ways',
      'Longest Increasing Subsequence', 'Longest Common Subsequence', 'Word Break', 'Partition Equal Subset Sum',
    ],
  },
];

const TEMPLATE_CONSTRAINTS = {
  twoSum: [
    '2 <= nums.length <= 10^4',
    '-10^9 <= nums[i] <= 10^9',
    '-10^9 <= target <= 10^9',
    'Exactly one valid answer exists.',
  ],
  slidingWindow: [
    '1 <= n <= 10^5',
    'Values fit 32-bit signed integers.',
    'Aim for O(n) time and O(1) extra space.',
  ],
  stack: [
    '1 <= s.length <= 10^4',
    's contains only parentheses characters.',
    'Use stack operations in O(n) time.',
  ],
  binarySearch: [
    '1 <= nums.length <= 10^5',
    'nums is sorted in ascending order.',
    'Target lookup should be O(log n).',
  ],
  intervals: [
    '1 <= intervals.length <= 10^4',
    '0 <= start <= end <= 10^4',
    'Sort and merge in O(n log n).',
  ],
  dp: [
    '1 <= n <= 45',
    'Use bottom-up or memoized recurrence.',
    'Optimize space where possible.',
  ],
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const slugify = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const ALGORITHMS = INTERVIEW_TRACKS.flatMap((group) => (
  group.questions.map((questionTitle) => {
    const template = TEMPLATE_LIBRARY[group.template];
    return {
      id: slugify(`${group.section}-${questionTitle}`),
      title: questionTitle,
      section: group.section,
      templateKey: group.template,
      pattern: template.pattern,
      companies: group.companies,
      difficulty: group.difficulty,
      visualType: template.visualType,
      question: {
        title: questionTitle,
        statement: `${template.statement} Interview variant: ${questionTitle}.`,
        description: `${template.statement} Solve ${questionTitle} efficiently using the ${template.pattern.toLowerCase()} approach.`,
        constraints: TEMPLATE_CONSTRAINTS[group.template],
        functionName: template.functionName,
        jsStarter: template.jsStarter,
        javaStarter: template.javaStarter,
        tests: template.tests,
      },
    };
  })
));

const VISUAL_SAMPLES = {
  'array-pointer': { nums: [2, 7, 11, 15], target: 9 },
  'binary-search': { nums: [-1, 0, 3, 5, 9, 12], target: 9 },
  stack: { text: '([{}])' },
  window: { prices: [7, 1, 5, 3, 6, 4] },
  intervals: { intervals: [[1, 3], [2, 6], [8, 10], [15, 18]] },
  dp: { n: 7 },
};

const buildVisualizationSteps = (visualType) => {
  if (visualType === 'array-pointer') {
    const { nums, target } = VISUAL_SAMPLES[visualType];
    const seen = new Map();
    const steps = [];
    for (let i = 0; i < nums.length; i += 1) {
      const need = target - nums[i];
      const match = seen.has(need) ? seen.get(need) : -1;
      const caption = match !== -1
        ? `Pointer at index ${i}. Found complement ${need} at index ${match}.`
        : `Pointer at index ${i}. Looking for complement ${need} in hash map.`;
      steps.push({ nums, current: i, match, seenKeys: [...seen.keys()], caption });
      if (match !== -1) break;
      seen.set(nums[i], i);
    }
    return steps;
  }

  if (visualType === 'binary-search') {
    const { nums, target } = VISUAL_SAMPLES[visualType];
    let left = 0;
    let right = nums.length - 1;
    const steps = [];
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const found = nums[mid] === target;
      const caption = found
        ? `Mid at index ${mid} equals target ${target}. Search complete.`
        : `Checking range [${left}, ${right}], mid index ${mid}, value ${nums[mid]}.`;
      steps.push({ nums, left, right, mid, found, caption });
      if (found) break;
      if (nums[mid] < target) left = mid + 1;
      else right = mid - 1;
    }
    return steps;
  }

  if (visualType === 'stack') {
    const { text } = VISUAL_SAMPLES[visualType];
    const pairs = new Map([[')', '('], [']', '['], ['}', '{']]);
    const stack = [];
    const steps = [];
    text.split('').forEach((ch, idx) => {
      let caption = '';
      if (pairs.has(ch)) {
        const popped = stack.pop();
        caption = `Read '${ch}', pop '${popped || ''}' and validate matching bracket.`;
      } else {
        stack.push(ch);
        caption = `Read '${ch}', push to stack.`;
      }
      steps.push({ text, current: idx, char: ch, stack: [...stack], caption });
    });
    return steps;
  }

  if (visualType === 'window') {
    const { prices } = VISUAL_SAMPLES[visualType];
    let minPrice = Infinity;
    let best = 0;
    const steps = [];
    prices.forEach((price, idx) => {
      minPrice = Math.min(minPrice, price);
      best = Math.max(best, price - minPrice);
      steps.push({
        prices,
        current: idx,
        minPrice,
        best,
        caption: `Day ${idx}: price=${price}, min so far=${minPrice}, best profit=${best}.`,
      });
    });
    return steps;
  }

  if (visualType === 'intervals') {
    const sorted = [...VISUAL_SAMPLES[visualType].intervals].sort((a, b) => a[0] - b[0]);
    const merged = [[...sorted[0]]];
    const steps = [{ sorted, current: 0, merged: merged.map((it) => [...it]), caption: 'Start with first interval.' }];
    for (let i = 1; i < sorted.length; i += 1) {
      const current = sorted[i];
      const last = merged[merged.length - 1];
      let caption;
      if (current[0] <= last[1]) {
        last[1] = Math.max(last[1], current[1]);
        caption = `Overlap detected. Merge current interval into previous merged range.`;
      } else {
        merged.push([...current]);
        caption = 'No overlap. Push as a new merged interval.';
      }
      steps.push({ sorted, current: i, merged: merged.map((it) => [...it]), caption });
    }
    return steps;
  }

  if (visualType === 'dp') {
    const { n } = VISUAL_SAMPLES[visualType];
    const dp = [0, 1, 2];
    const steps = [
      { n, idx: 1, dp: [1], caption: 'Base case: ways(1)=1.' },
      { n, idx: 2, dp: [1, 2], caption: 'Base case: ways(2)=2.' },
    ];
    for (let i = 3; i <= n; i += 1) {
      dp[i] = dp[i - 1] + dp[i - 2];
      steps.push({
        n,
        idx: i,
        dp: dp.slice(1, i + 1),
        caption: `ways(${i}) = ways(${i - 1}) + ways(${i - 2}) = ${dp[i]}.`,
      });
    }
    return steps;
  }

  return [];
};

const serializeForJava = (value) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return 'new int[]{}';
    if (Array.isArray(value[0])) {
      return `new int[][]{${value.map((it) => `{${it.join(', ')}}`).join(', ')}}`;
    }
    return `new int[]{${value.join(', ')}}`;
  }
  if (typeof value === 'string') return `\"${value.replace(/\\/g, '\\\\').replace(/\"/g, '\\\"')}\"`;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

const buildJavaRunner = (algorithm, code) => {
  const testEntries = algorithm.question.tests
    .map((test, idx) => {
      const args = test.input.map((arg) => serializeForJava(arg)).join(', ');
      const expected = serializeForJava(test.expected);
      return `runTest(${idx}, Solution.${algorithm.question.functionName}(${args}), ${expected});`;
    })
    .join('\n    ');

  return `${code}

public class Main {
  private static boolean deepEquals(Object a, Object b) {
    if (a == null || b == null) return a == b;
    if (a instanceof int[] && b instanceof int[]) return java.util.Arrays.equals((int[]) a, (int[]) b);
    if (a instanceof int[][] && b instanceof int[][]) return java.util.Arrays.deepEquals((int[][]) a, (int[][]) b);
    return a.equals(b);
  }

  private static String stringify(Object val) {
    if (val instanceof int[]) return java.util.Arrays.toString((int[]) val);
    if (val instanceof int[][]) return java.util.Arrays.deepToString((int[][]) val);
    return String.valueOf(val);
  }

  private static void runTest(int index, Object actual, Object expected) {
    boolean ok = deepEquals(actual, expected);
    System.out.println("__RESULT__:" + index + ":" + (ok ? "1" : "0") + ":" + stringify(actual));
  }

  public static void main(String[] args) {
    ${testEntries}
  }
}`;
};

const buildJavaScriptRunner = (algorithm, code) => {
  const testsLiteral = JSON.stringify(algorithm.question.tests);
  return `${code}

const tests = ${testsLiteral};

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

tests.forEach((t, index) => {
  try {
    const actual = ${algorithm.question.functionName}(...t.input);
    const ok = same(actual, t.expected);
    console.log('__RESULT__:' + index + ':' + (ok ? '1' : '0') + ':' + JSON.stringify(actual));
  } catch (err) {
    console.log('__RESULT__:' + index + ':0:ERROR:' + err.message);
  }
});`;
};

const parseRunOutput = (stdout, testsCount) => {
  const lines = stdout.split('\n').filter((line) => line.startsWith('__RESULT__:'));
  const results = lines.map((line) => {
    const parts = line.split(':');
    const index = Number(parts[1]);
    const passed = parts[2] === '1';
    const actual = parts.slice(3).join(':');
    return { index, passed, actual };
  });

  if (results.length < testsCount) {
    const observed = new Set(results.map((r) => r.index));
    for (let i = 0; i < testsCount; i += 1) {
      if (!observed.has(i)) results.push({ index: i, passed: false, actual: 'No result' });
    }
  }
  return results.sort((a, b) => a.index - b.index);
};

const executeCode = async (language, source) => {
  const body = {
    language,
    version: '*',
    files: [{ content: source }],
  };

  const response = await fetch('https://emkc.org/api/v2/piston/execute', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) throw new Error(`Execution failed with status ${response.status}`);
  return response.json();
};

const readLocalProgress = (userKey) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(PROGRESS_STORAGE_KEY) || '{}');
    return parsed[userKey] || { solved: [] };
  } catch (_) {
    return { solved: [] };
  }
};

const writeLocalProgress = (userKey, payload) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(PROGRESS_STORAGE_KEY) || '{}');
    parsed[userKey] = payload;
    localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(parsed));
  } catch (_) {
    // no-op fallback
  }
};

const AlgorithmVisualizer = ({ algorithm, step }) => {
  if (!algorithm || !step) return null;

  if (algorithm.visualType === 'array-pointer' || algorithm.visualType === 'binary-search') {
    const arr = step.nums;
    return (
      <div className="dsa-array-wrap">
        {arr.map((num, idx) => {
          const isCurrent = idx === step.current || idx === step.mid;
          const isMatch = idx === step.match || idx === step.left || idx === step.right;
          const isFound = step.found && idx === step.mid;
          return (
            <div
              key={`${algorithm.id}-${idx}`}
              className={`dsa-box ${isCurrent ? 'current' : ''} ${isMatch ? 'focus' : ''} ${isFound ? 'found' : ''}`}
            >
              <span className="dsa-index">{idx}</span>
              <span>{num}</span>
            </div>
          );
        })}
      </div>
    );
  }

  if (algorithm.visualType === 'stack') {
    return (
      <div className="dsa-stack-layout">
        <div className="dsa-char-row">
          {step.text.split('').map((ch, idx) => (
            <div key={`${ch}-${idx}`} className={`dsa-char-box ${idx === step.current ? 'active' : ''}`}>
              {ch}
            </div>
          ))}
        </div>
        <div className="dsa-stack">
          {step.stack.length === 0 ? <p className="stack-empty">Stack Empty</p> : null}
          {step.stack.map((item, idx) => (
            <div key={`${item}-${idx}`} className="dsa-stack-item">{item}</div>
          ))}
        </div>
      </div>
    );
  }

  if (algorithm.visualType === 'window') {
    return (
      <div className="dsa-array-wrap">
        {step.prices.map((num, idx) => (
          <div key={`${algorithm.id}-${idx}`} className={`dsa-box ${idx === step.current ? 'current' : ''}`}>
            <span className="dsa-index">{idx}</span>
            <span>{num}</span>
          </div>
        ))}
        <div className="dsa-metric">Min: {step.minPrice}</div>
        <div className="dsa-metric">Best Profit: {step.best}</div>
      </div>
    );
  }

  if (algorithm.visualType === 'intervals') {
    return (
      <div className="dsa-intervals-layout">
        <div className="interval-track">
          {step.sorted.map((interval, idx) => (
            <div key={`sorted-${idx}`} className={`interval-chip ${idx === step.current ? 'active' : ''}`}>
              [{interval[0]}, {interval[1]}]
            </div>
          ))}
        </div>
        <div className="interval-track merged">
          {step.merged.map((interval, idx) => (
            <div key={`merged-${idx}`} className="interval-chip merged-chip">
              [{interval[0]}, {interval[1]}]
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (algorithm.visualType === 'dp') {
    return (
      <div className="dsa-array-wrap">
        {step.dp.map((val, idx) => (
          <div key={`${val}-${idx}`} className={`dsa-box ${idx + 1 === step.idx ? 'current' : ''}`}>
            <span className="dsa-index">step {idx + 1}</span>
            <span>{val}</span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

const DSALabPage = () => {
  const { currentUser } = useAuth();

  const [selectedAlgorithmId, setSelectedAlgorithmId] = useState(ALGORITHMS[0].id);
  const [isPlaying, setIsPlaying] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const [language, setLanguage] = useState('javascript');
  const [code, setCode] = useState(ALGORITHMS[0].question.jsStarter);
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [runError, setRunError] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('All Companies');
  const [openAccordion, setOpenAccordion] = useState(INTERVIEW_TRACKS[0].section);
  const [progress, setProgress] = useState({ solved: [] });
  const [problemPaneWidth, setProblemPaneWidth] = useState(47);
  const [testPaneHeight, setTestPaneHeight] = useState(240);
  const [testsCollapsed, setTestsCollapsed] = useState(false);
  const [activeTestCase, setActiveTestCase] = useState(0);
  const workspaceRef = useRef(null);
  const editorPaneRef = useRef(null);
  const dragRef = useRef(null);

  const companies = useMemo(() => {
    const all = Array.from(new Set(ALGORITHMS.flatMap((algo) => algo.companies))).sort();
    return ['All Companies', ...all];
  }, []);

  const filteredAlgorithms = useMemo(() => {
    if (selectedCompany === 'All Companies') return ALGORITHMS;
    return ALGORITHMS.filter((algo) => algo.companies.includes(selectedCompany));
  }, [selectedCompany]);

  useEffect(() => {
    if (!filteredAlgorithms.find((algo) => algo.id === selectedAlgorithmId)) {
      setSelectedAlgorithmId(filteredAlgorithms[0]?.id || ALGORITHMS[0].id);
    }
  }, [filteredAlgorithms, selectedAlgorithmId]);

  const selectedAlgorithm = useMemo(
    () => ALGORITHMS.find((algo) => algo.id === selectedAlgorithmId) || ALGORITHMS[0],
    [selectedAlgorithmId],
  );

  const steps = useMemo(() => buildVisualizationSteps(selectedAlgorithm.visualType), [selectedAlgorithm.visualType]);
  const currentStep = steps[Math.min(stepIndex, Math.max(steps.length - 1, 0))];

  const userProgressKey = currentUser ? `user_${currentUser.id}` : 'guest';

  useEffect(() => {
    const loadProgress = async () => {
      const local = readLocalProgress(userProgressKey);
      setProgress(local);
      if (!currentUser) return;

      try {
        const progressRef = doc(db, 'dsaProgress', userProgressKey);
        const snap = await getDoc(progressRef);
        if (snap.exists()) {
          const data = snap.data();
          const next = { solved: data.solved || [] };
          setProgress(next);
          writeLocalProgress(userProgressKey, next);
        }
      } catch (_) {
        // Keep local fallback.
      }
    };

    loadProgress();
  }, [currentUser, userProgressKey]);

  useEffect(() => {
    setStepIndex(0);
    setResults([]);
    setRunError('');
    setActiveTestCase(0);
    setCode(language === 'javascript' ? selectedAlgorithm.question.jsStarter : selectedAlgorithm.question.javaStarter);
  }, [selectedAlgorithm.id, language]);

  const onHorizontalSplitMouseDown = useCallback((event) => {
    event.preventDefault();
    dragRef.current = { type: 'horizontal' };
    document.body.style.cursor = 'col-resize';
  }, []);

  const onVerticalSplitMouseDown = useCallback((event) => {
    event.preventDefault();
    dragRef.current = { type: 'vertical' };
    document.body.style.cursor = 'row-resize';
  }, []);

  useEffect(() => {
    const onMouseMove = (event) => {
      if (!dragRef.current) return;
      if (dragRef.current.type === 'horizontal' && workspaceRef.current) {
        const rect = workspaceRef.current.getBoundingClientRect();
        const nextWidth = ((event.clientX - rect.left) / rect.width) * 100;
        setProblemPaneWidth(clamp(nextWidth, 28, 72));
      }

      if (dragRef.current.type === 'vertical' && editorPaneRef.current) {
        const rect = editorPaneRef.current.getBoundingClientRect();
        const nextHeight = rect.bottom - event.clientY;
        setTestPaneHeight(clamp(nextHeight, 130, rect.height - 160));
      }
    };

    const onMouseUp = () => {
      dragRef.current = null;
      document.body.style.cursor = '';
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  useEffect(() => {
    if (!isPlaying || steps.length <= 1) return undefined;
    const intervalId = window.setInterval(() => {
      setStepIndex((prev) => ((prev + 1) % steps.length));
    }, 1200);
    return () => window.clearInterval(intervalId);
  }, [isPlaying, steps.length]);

  const persistProgress = async (nextProgress) => {
    setProgress(nextProgress);
    writeLocalProgress(userProgressKey, nextProgress);
    if (!currentUser) return;

    try {
      await setDoc(doc(db, 'dsaProgress', userProgressKey), {
        solved: nextProgress.solved,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (_) {
      // Silent fallback to local storage.
    }
  };

  const handleRunTests = async () => {
    setIsRunning(true);
    setRunError('');
    setResults([]);

    try {
      const runner = language === 'javascript'
        ? buildJavaScriptRunner(selectedAlgorithm, code)
        : buildJavaRunner(selectedAlgorithm, code);
      const response = await executeCode(language, runner);

      if (response.run && response.run.stderr) {
        const hasResultLines = (response.run.stdout || '').includes('__RESULT__:');
        if (!hasResultLines) {
          throw new Error(response.run.stderr);
        }
      }

      const parsed = parseRunOutput(response.run.stdout || '', selectedAlgorithm.question.tests.length);
      setResults(parsed);

      const allPassed = parsed.length > 0 && parsed.every((entry) => entry.passed);
      if (allPassed && !progress.solved.includes(selectedAlgorithm.id)) {
        await persistProgress({ solved: [...progress.solved, selectedAlgorithm.id] });
      }
    } catch (err) {
      setRunError(err.message || 'Execution failed. Please try again.');
    } finally {
      setIsRunning(false);
    }
  };

  const passCount = results.filter((r) => r.passed).length;
  const solvedCount = progress.solved.length;
  const activeCase = selectedAlgorithm.question.tests[activeTestCase] || selectedAlgorithm.question.tests[0];
  const resultByCase = useMemo(
    () => Object.fromEntries(results.map((entry) => [entry.index, entry])),
    [results],
  );

  return (
    <div className="dsa-lab-page">
      <SEOMeta
        title="DSA Visual Lab and Interview Practice | Algodoor"
        description="Learn data structures and algorithms with interactive visual animations and run interview-style coding challenges in JavaScript and Java."
        url="/dsa-lab"
        type="WebPage"
        image="https://images.unsplash.com/photo-1515879218367-8466d910aaa4?w=1200&auto=format&fit=crop&q=80"
        schemaMarkup={{
          '@context': 'https://schema.org',
          '@graph': [generateOrganizationSchema(), generateLocalBusinessSchema()],
        }}
      />

      <div className="gradient__bg">
        <Navbar />
      </div>

      <section className="dsa-hero">
        <div className="hero-glow" aria-hidden="true" />
        <h1>DSA Visual Lab for Top Company Interviews</h1>
        <p>
          Explore 48 interview-focused algorithm variants with animated iterators and coding challenges.
          Filter by company, solve questions, and track your progress automatically.
        </p>
        <div className="progress-strip">
          <span>Total Problems: {ALGORITHMS.length}</span>
          <span>Solved: {solvedCount}</span>
          <span>Active Company: {selectedCompany}</span>
        </div>
      </section>

      <section className="dsa-main-grid">
        <div className="dsa-left-panel">
          <h2>Interactive Visualizer</h2>

          <div className="company-filter-row">
            <label htmlFor="company-select">Company Focus</label>
            <select
              id="company-select"
              value={selectedCompany}
              onChange={(event) => setSelectedCompany(event.target.value)}
            >
              {companies.map((company) => (
                <option key={company} value={company}>{company}</option>
              ))}
            </select>
          </div>

          <div className="algorithm-picker">
            {filteredAlgorithms.map((algo) => (
              <button
                key={algo.id}
                type="button"
                onClick={() => setSelectedAlgorithmId(algo.id)}
                className={selectedAlgorithm.id === algo.id ? 'active' : ''}
              >
                {algo.title}
                {progress.solved.includes(algo.id) ? ' ✓' : ''}
              </button>
            ))}
          </div>

          <div className="algo-meta">
            <h3>{selectedAlgorithm.title}</h3>
            <p>Family: {selectedAlgorithm.section}</p>
            <p>Pattern: {selectedAlgorithm.pattern}</p>
            <p>Difficulty: {selectedAlgorithm.difficulty}</p>
            <p>Asked by: {selectedAlgorithm.companies.join(', ')}</p>
          </div>

          <div className="visual-stage">
            <AlgorithmVisualizer algorithm={selectedAlgorithm} step={currentStep} />
          </div>
          <p className="visual-caption">{currentStep?.caption || 'Visualizer initialized.'}</p>

          <div className="player-controls">
            <button type="button" onClick={() => setIsPlaying((prev) => !prev)}>
              {isPlaying ? 'Pause' : 'Play'}
            </button>
            <button
              type="button"
              onClick={() => setStepIndex((prev) => Math.max(prev - 1, 0))}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setStepIndex((prev) => Math.min(prev + 1, Math.max(steps.length - 1, 0)))}
            >
              Next
            </button>
            <span>Step {Math.min(stepIndex + 1, Math.max(steps.length, 1))}/{Math.max(steps.length, 1)}</span>
          </div>
        </div>

        <div className="dsa-right-panel">
          <h2>Selected Problem</h2>
          <h3>{selectedAlgorithm.question.title}</h3>
          <p>{selectedAlgorithm.question.statement}</p>
          <p>Open the coding workspace below to read full description, examples, constraints, and run tests.</p>
          <div className="results-box compact">
            <h4>Run Summary</h4>
            <p>{results.length ? `${passCount}/${results.length} test cases passed` : 'No testcase run yet.'}</p>
          </div>
        </div>
      </section>

      <section className="leetcode-workspace-section">
        <div className="leetcode-workspace" ref={workspaceRef}>
          <article className="problem-pane" style={{ width: `${problemPaneWidth}%` }}>
            <div className="problem-headline">
              <h3>{selectedAlgorithm.question.title}</h3>
              <span className="difficulty-chip">{selectedAlgorithm.difficulty}</span>
            </div>
            <p className="problem-statement">{selectedAlgorithm.question.statement}</p>

            <h4>Description</h4>
            <p>{selectedAlgorithm.question.description}</p>

            <h4>Examples</h4>
            <div className="problem-examples">
              {selectedAlgorithm.question.tests.map((test, idx) => (
                <div key={`example-${idx}`} className="problem-example-card">
                  <h5>Example {idx + 1}</h5>
                  <p><strong>Input:</strong> {JSON.stringify(test.input)}</p>
                  <p><strong>Output:</strong> {JSON.stringify(test.expected)}</p>
                </div>
              ))}
            </div>

            <h4>Constraints</h4>
            <ul className="constraint-list">
              {selectedAlgorithm.question.constraints.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <div
            className="workspace-splitter vertical"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize problem and editor panels"
            onMouseDown={onHorizontalSplitMouseDown}
          />

          <article className="editor-pane" ref={editorPaneRef}>
            <div className="runner-topbar leetcode-topbar">
              <label htmlFor="language-select">Language</label>
              <select
                id="language-select"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="javascript">JavaScript</option>
                <option value="java">Java</option>
              </select>
              <button type="button" onClick={handleRunTests} disabled={isRunning}>
                {isRunning ? 'Running...' : 'Run Test Cases'}
              </button>
            </div>

            <div className="editor-body">
              <textarea
                className="code-editor leetcode-editor"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                spellCheck={false}
              />
            </div>

            {!testsCollapsed && (
              <div
                className="workspace-splitter horizontal"
                role="separator"
                aria-orientation="horizontal"
                aria-label="Resize editor and testcase panels"
                onMouseDown={onVerticalSplitMouseDown}
              />
            )}

            <div
              className={`testcase-pane ${testsCollapsed ? 'collapsed' : ''}`}
              style={{ height: testsCollapsed ? 42 : `${testPaneHeight}px` }}
            >
              <div className="testcase-pane-header">
                <div className="testcase-pane-title-wrap">
                  <strong>Testcases</strong>
                  <span>
                    {results.length ? `${passCount}/${results.length} passed` : 'Not executed'}
                  </span>
                </div>
                <button type="button" onClick={() => setTestsCollapsed((prev) => !prev)}>
                  {testsCollapsed ? 'Expand' : 'Collapse'}
                </button>
              </div>

              {!testsCollapsed && (
                <div className="testcase-pane-content">
                  <div className="testcase-tabs">
                    {selectedAlgorithm.question.tests.map((_, idx) => {
                      const row = resultByCase[idx];
                      return (
                        <button
                          key={`tab-${idx}`}
                          type="button"
                          className={`testcase-tab ${activeTestCase === idx ? 'active' : ''} ${row ? (row.passed ? 'passed' : 'failed') : ''}`}
                          onClick={() => setActiveTestCase(idx)}
                        >
                          Case {idx + 1}
                        </button>
                      );
                    })}
                  </div>

                  <div className="testcase-detail">
                    <p><strong>Input:</strong> {JSON.stringify(activeCase?.input)}</p>
                    <p><strong>Expected:</strong> {JSON.stringify(activeCase?.expected)}</p>
                    <p>
                      <strong>Result:</strong>{' '}
                      {resultByCase[activeTestCase]
                        ? (resultByCase[activeTestCase].passed ? 'Passed' : `Failed (${resultByCase[activeTestCase].actual})`)
                        : 'Run test cases to see output'}
                    </p>
                    {runError ? <p className="run-error">{runError}</p> : null}
                  </div>
                </div>
              )}
            </div>
          </article>
        </div>
      </section>

      <section className="catalog-section">
        <h2>Algorithm Question Accordions</h2>
        <p>Each accordion groups related interview questions by algorithm family pattern.</p>

        <div className="accordion-shell">
          {INTERVIEW_TRACKS.map((group) => {
            const isOpen = openAccordion === group.section;
            return (
              <div key={group.section} className="accordion-card">
                <button
                  type="button"
                  className={`accordion-title ${isOpen ? 'open' : ''}`}
                  onClick={() => setOpenAccordion((prev) => (prev === group.section ? '' : group.section))}
                >
                  {group.section}
                  <span>{isOpen ? '−' : '+'}</span>
                </button>

                {isOpen && (
                  <div className="accordion-content">
                    {group.questions.map((question) => {
                      const id = slugify(`${group.section}-${question}`);
                      const isSolved = progress.solved.includes(id);
                      return (
                        <button
                          key={question}
                          type="button"
                          className={`accordion-question ${selectedAlgorithm.id === id ? 'selected' : ''}`}
                          onClick={() => setSelectedAlgorithmId(id)}
                        >
                          {question}
                          <span>{isSolved ? 'Solved' : 'Practice'}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DSALabPage;
